/**
 * One check, rendered by its response type.
 *
 * The control an engineer sees is decided by data, not by a switch someone has
 * to remember to extend: a measured check gets a numeric field and a live
 * verdict, a judged check gets buttons. Getting this wrong is not cosmetic — a
 * measured check rendered as a pass/fail button means the reading is never
 * captured at all, and the report has a verdict with no number behind it.
 *
 * The band is always shown, before and after the reading. An engineer who can
 * see the target while typing catches a transposed digit; one who cannot, does
 * not.
 */

import { memo, useEffect, useState } from 'react';
import type { CheckView } from '../domain/capture.js';
import type { ResultInput } from '../domain/verdict.js';
import type { Verdict } from '../domain/types.js';

export interface CheckRowProps {
  check: CheckView;
  onChange: (input: ResultInput) => void;
  /** True when this check's acceptance limit was moved from the project default. */
  limitMoved?: boolean;
  disabled?: boolean;
}

const VERDICT_STYLE: Record<Verdict, string> = {
  Pass: 'pass',
  Fail: 'fail',
  'N/A': 'na',
  Pending: 'pend',
};

/** The options an engineer can pick, by response type. */
function optionsFor(responseType: string): Verdict[] {
  return responseType === 'yes_no_applicable'
    ? ['Pass', 'Fail', 'N/A'] // rendered as Yes / No / N/A
    : ['Pass', 'Fail', 'N/A'];
}

const YES_NO: Partial<Record<Verdict, string>> = { Pass: 'Yes', Fail: 'No', 'N/A': 'N/A' };
const labelFor = (responseType: string, v: Verdict): string =>
  (responseType === 'yes_no_applicable' ? YES_NO[v] : undefined) ?? v;

/**
 * What a typed reading means.
 *
 * Empty is "not yet measured", never zero: clearing a field must return the
 * check to Pending rather than record 0 and fail it. Anything else unparseable
 * is treated the same way — the text stays on screen so the engineer can see
 * what they typed, but nothing numeric is recorded from it.
 */
export function parseReading(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  // `Number` accepts '', '0x10' and whitespace; a reading is a plain decimal.
  // A trailing point ("0.") is half-typed, not a reading of zero: recording it
  // would flash a Fail on screen while the engineer is still typing 0.35.
  if (!/^[-+]?(\d+(\.\d+)?|\.\d+)$/.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export const CheckRow = memo(function CheckRow({ check, onChange, limitMoved, disabled }: CheckRowProps) {
  const { item, input, verdict, band, reason } = check;
  const measured = item.responseType === 'measured_pass_fail';
  const fieldId = `check-${item.id}`;

  /**
   * The field holds the text, not the number.
   *
   * Binding the input straight to the parsed value makes a decimal point
   * impossible to type: "0." parses to 0, the field re-renders as "0", and the
   * engineer typing 0.5 Ω ends up with 5. Readings on this platform are
   * decimals — 212.5 A, 0.35 Ω — so the text is kept here and the number is
   * what leaves.
   */
  const [text, setText] = useState(() => (input.measuredValue ?? '').toString());
  useEffect(() => {
    // Follow the value when it changes from elsewhere — a different breaker
    // rendering into the same row, or an imported reading — but never fight the
    // engineer mid-keystroke.
    const incoming = input.measuredValue;
    if (incoming === null || incoming === undefined) {
      if (parseReading(text) !== null) setText('');
    } else if (parseReading(text) !== incoming) {
      setText(String(incoming));
    }
    // `text` is deliberately not a dependency: this syncs from the value in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.measuredValue, item.id]);

  return (
    <div className="check" data-verdict={verdict} data-testid={`check-${item.id}`}>
      <div className="check-head">
        <label className="check-label" htmlFor={fieldId}>
          {item.label}
          {item.unit ? <span className="unit"> ({item.unit})</span> : null}
        </label>
        <span className={`chip ${VERDICT_STYLE[verdict]}`} data-testid={`verdict-${item.id}`}>
          {verdict}
        </span>
      </div>

      {band ? (
        <p className="criteria">
          {band}
          {limitMoved ? <span className="moved"> · project limit</span> : null}
        </p>
      ) : null}

      {measured ? (
        <div className="check-input">
          <input
            id={fieldId}
            type="text"
            inputMode="decimal"
            aria-describedby={`${fieldId}-reason`}
            value={text}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              setText(raw);
              onChange({ measuredValue: parseReading(raw) });
            }}
          />
          {item.unit ? <span className="unit">{item.unit}</span> : null}
          <button
            type="button"
            className={input.selected === 'N/A' ? 'na on' : 'na'}
            aria-pressed={input.selected === 'N/A'}
            disabled={disabled}
            onClick={() =>
              onChange(input.selected === 'N/A' ? { measuredValue: input.measuredValue ?? null } : { selected: 'N/A' })
            }
          >
            N/A
          </button>
        </div>
      ) : (
        <div className="judge" role="group" aria-label={item.label}>
          {optionsFor(item.responseType).map((v) => (
            <button
              key={v}
              type="button"
              data-value={v}
              aria-pressed={input.selected === v}
              disabled={disabled}
              onClick={() => onChange({ selected: v })}
            >
              {labelFor(item.responseType, v)}
            </button>
          ))}
        </div>
      )}

      <p className="reason" id={`${fieldId}-reason`}>
        {reason}
      </p>
    </div>
  );
});
